/**
 * Cloudflare image resize helper for assets.auxodev.dk
 */
export function getOptimizedImageUrl(url, options = {}) {
  if (!url || typeof url !== 'string') return ''
  // Only transform URLs from our asset CDN
  if (!url.includes('assets.auxodev.dk') || url.includes('/cdn-cgi/image/')) {
    return url
  }

  const { width, height, quality = 80, format = 'auto', fit = 'cover' } = options
  const parts = []
  if (width) parts.push(`width=${width}`)
  if (height) {
    parts.push(`height=${height}`)
    parts.push(`fit=${fit}`)
  }
  parts.push(`quality=${quality}`)
  parts.push(`format=${format}`)

  const optionsString = parts.join(',')
  return url.replace('https://assets.auxodev.dk/', `https://assets.auxodev.dk/cdn-cgi/image/${optionsString}/`)
}

// Fallback galleries in case public endpoint is loading or offline
export const FALLBACK_GALLERIES = {
  kabeltraek: {
    title: 'Kabeltræk',
    description: 'Se billeder af vores kabelaftrækkersystem på minilæsser, 500m kabeltromler og kabelgravning til private arealer.',
    images: [
      'https://assets.auxodev.dk/assets/ea5c97a8-a563-48d7-8fa0-939f59c0351d/197fcec7-f684-426c-9d53-5408d6004b06.jpg',
      'https://assets.auxodev.dk/assets/ea5c97a8-a563-48d7-8fa0-939f59c0351d/kabeltraek-minilaesser-maskine.jpg',
      'https://assets.auxodev.dk/assets/ea5c97a8-a563-48d7-8fa0-939f59c0351d/kabeltraek-kabelruller-500m.jpg',
      'https://assets.auxodev.dk/assets/ea5c97a8-a563-48d7-8fa0-939f59c0351d/kabelgravning-privat-have.jpg',
    ],
  },
  liftarbejde: {
    title: 'Liftarbejde',
    description: 'Egen saxelift (8m arbejdshøjde) og mobil Omme bomlift (10,5m arbejdshøjde) til opgaver i højden, tagrender og beskæring.',
    images: [
      'https://assets.auxodev.dk/assets/ea5c97a8-a563-48d7-8fa0-939f59c0351d/liftarbejde-saxelift-indendoers.jpg',
      'https://assets.auxodev.dk/assets/ea5c97a8-a563-48d7-8fa0-939f59c0351d/liftarbejde-omme-bomlift-have.jpg',
      'https://assets.auxodev.dk/assets/ea5c97a8-a563-48d7-8fa0-939f59c0351d/liftarbejde-beskaering-tagrender.jpg',
      'https://assets.auxodev.dk/assets/ea5c97a8-a563-48d7-8fa0-939f59c0351d/fd248068-b038-4b6e-bb81-80419e4e7af8.jpg',
    ],
  },
  'el-arbejde': {
    title: 'El-arbejde',
    description: 'Almindelige el-opgaver for private og erhverv, eltavler, renovering og nybyggeri med 25 års erfaring.',
    images: [
      'https://assets.auxodev.dk/assets/ea5c97a8-a563-48d7-8fa0-939f59c0351d/el-arbejde-eltavle-installation.jpg',
      'https://assets.auxodev.dk/assets/ea5c97a8-a563-48d7-8fa0-939f59c0351d/el-arbejde-renovering-installation.jpg',
      'https://assets.auxodev.dk/assets/ea5c97a8-a563-48d7-8fa0-939f59c0351d/el-arbejde-vaerktoej-service.jpg',
      'https://assets.auxodev.dk/assets/ea5c97a8-a563-48d7-8fa0-939f59c0351d/fd248068-b038-4b6e-bb81-80419e4e7af8.jpg',
    ],
  },
  designerlamper: {
    title: 'Designerlamper',
    description: 'Professionel montering og ophængning af danske designerlamper med rådgivning om placering og tilbud.',
    images: [
      'https://assets.auxodev.dk/assets/ea5c97a8-a563-48d7-8fa0-939f59c0351d/designerlamper-spisebord-pendler.jpg',
      'https://assets.auxodev.dk/assets/ea5c97a8-a563-48d7-8fa0-939f59c0351d/designerlamper-arkitektonisk-belysning.jpg',
      'https://assets.auxodev.dk/assets/ea5c97a8-a563-48d7-8fa0-939f59c0351d/designerlamper-praecisionsmontering.jpg',
    ],
  },
}

/**
 * Fetch all galleries from CMS
 */
export async function fetchCmsGalleries() {
  try {
    const res = await fetch('https://api.dev.auxo.dk/public/content/galleri')
    if (!res.ok) throw new Error(`Status ${res.status}`)
    const data = await res.json()
    if (Array.isArray(data) && data.length > 0) {
      return data
    }
  } catch (err) {
    console.warn('Using fallback galleries:', err)
  }
  return Object.entries(FALLBACK_GALLERIES).map(([slug, item]) => ({
    slug: { _: slug },
    ...item,
  }))
}

/**
 * Fetch a single gallery by slug from CMS
 */
export async function fetchCmsGalleryBySlug(slug) {
  const cleanSlug = slug.replace(/-pages$/, '')
  try {
    const galleries = await fetchCmsGalleries()
    const found = galleries.find(g => {
      const s = typeof g.slug === 'object' ? g.slug?._ || g.slug?.da : g.slug
      return s === cleanSlug || s === slug
    })
    if (found) return found
  } catch (err) {
    console.warn('Error fetching gallery by slug:', err)
  }
  return FALLBACK_GALLERIES[cleanSlug] || null
}

/**
 * Fetch all pages from CMS
 */
export async function fetchCmsPages() {
  try {
    const res = await fetch('https://api.dev.auxo.dk/public/content/pages')
    if (!res.ok) throw new Error(`Status ${res.status}`)
    const data = await res.json()
    if (Array.isArray(data) && data.length > 0) {
      return data
    }
  } catch (err) {
    console.warn('Error fetching pages from CMS:', err)
  }
  return []
}
